import { reactive } from 'vue'
import { fromQuery, toQuery } from 'query-string-parser'
import axios from 'axios'

// Fields holding a single value that may itself contain commas (e.g. a location label)
const SINGLE_VALUE_FIELDS = ['location', 'start-guaranteed', 'available']

let latestRequest = 0

export default reactive({
    bundleId: null,
    loading: false,
    loaded: false,
    error: false,
    data: {
        items: [],
        facets: [],
        meta: {},
    },
    filters: {},
    size: 50,
    offset: 0,
    append: false,
    get activeFilterCount () {
        return Object.keys(this.filters).length
    },
    get locationFilter () {
        const value = this.filters.location?.[0]

        if (typeof value !== 'string') {
            return null
        }

        const [lat, lng, distance, label] = value.split(':')

        return {
            lat,
            lng,
            distance: Number(distance) || 50,
            label: label || '',
        }
    },
    init (bundleId) {
        this.bundleId = bundleId

        const params = fromQuery(location.search) || {}

        if (params.f) {
            for (const field in params.f) {
                this.filters[field] = SINGLE_VALUE_FIELDS.includes(field)
                    ? [params.f[field]]
                    : params.f[field].split(',')
            }
        }

        if (params.s) {
            this.size = params.s
        }
    },
    updateQueryParams () {
        const params = {}

        if (Object.keys(this.filters).length > 0) {
            params.f = {}
            for (const field in this.filters) {
                if (this.filters[field].length > 0) {
                    params.f[field] = this.filters[field].join(',')
                }
            }
        }

        if (this.size > 0) {
            params.s = this.size
        }

        const queryString = Object.keys(params).length > 0 ? '?' + decodeURI(toQuery(params)) : ''
        window.history.replaceState(null, '', window.location.pathname + queryString + window.location.hash)
    },
    nextPage () {
        this.offset = parseInt(this.offset) + parseInt(this.size)
        this.append = true
        this.search()
    },
    toggleFilter (field, value, override) {
        if (field) {
            const overrideFilter = override || false

            if (!this.filters[field]) {
                this.filters[field] = []
            }

            if (overrideFilter) {
                if (value !== null && this.filters[field][0] !== value) {
                    this.filters[field] = [value]
                } else {
                    delete this.filters[field]
                }
            } else {
                if (this.filters[field].includes(value)) {
                    this.filters[field].splice(this.filters[field].indexOf(value), 1)
                } else {
                    this.filters[field].push(value)
                }
            }

            if (this.filters[field] && this.filters[field].length === 0) {
                delete this.filters[field]
            }
        }

        this.newSearch()
    },
    isFilterOptionActive (field, value) {
        return this.filters[field]?.includes(value) ?? false
    },
    getFacetByField (field) {
        return this.data.facets?.find(facet => facet.field === field) ?? null
    },
    setFilters (filters) {
        for (const field in filters) {
            this.filters[field] = filters[field]
        }
    },
    setFilter (field, value) {
        this.filters[field] = value
        this.newSearch()
    },
    deleteFilter (field) {
        delete this.filters[field]
        this.newSearch()
    },
    // Clears several fields with a single search
    deleteFilters (fields) {
        fields.forEach(field => delete this.filters[field])
        this.newSearch()
    },
    reset () {
        this.filters = {}
        this.newSearch()
    },
    // Start over from the first page, e.g. after the filters changed
    newSearch () {
        this.offset = 0
        this.append = false
        this.search()
    },
    search () {
        const request = ++latestRequest

        this.loading = true
        this.error = false

        // Default params
        let params = {
            s: this.size,
            o: this.offset,
        }

        // Filters
        if (Object.keys(this.filters).length > 0) {
            params.f = {}

            for (const field in this.filters) {
                if (this.filters[field].length > 0) {
                    params.f[field] = this.filters[field].join(',')
                }
            }
        }

        axios
            .get(`/api/bundle/${this.bundleId}/schedule/search`, {
                params: params
            })
            .then(response => {
                // Ignore responses that were overtaken by a newer search
                if (request !== latestRequest) {
                    return
                }

                if (this.append) {
                    this.data.items = this.data.items.concat(response.data.items)
                    this.data.meta = response.data.meta
                } else {
                    this.data = response.data
                }

                this.loaded = true
            })
            .catch(() => {
                if (request === latestRequest) {
                    this.error = true
                }
            })
            .finally(() => {
                if (request === latestRequest) {
                    this.loading = false
                    this.updateQueryParams()
                }
            })
    }
})
